import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { ThemeProvider } from "@/hooks/use-theme";
import { AuthProvider } from "@/contexts/AuthContext";
import DashboardPage from "./pages/DashboardPage";
import SolicitacoesPage from "./pages/SolicitacoesPage";
import AgentesPage from "./pages/AgentesPage";
import { AgentesErrorBoundary } from "@/components/error/AgentesErrorBoundary";
import OuvidoriaPage from "./pages/OuvidoriaPage";
import { GestorRoute } from "@/components/auth/GestorRoute";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import OuvidoriasSupervisionadasPage from "./pages/OuvidoriasSupervisionadasPage";
import OuvidoriaSupervisionadaDetalhesPage from "./pages/OuvidoriaSupervisionadaDetalhesPage";
import SupervisoraPage from "./pages/SupervisoraPage";
import RelatoriosPage from "./pages/RelatoriosPage";
import SolicitacaoDetalhesPage from "./pages/SolicitacaoDetalhesPage";
import NotificacaoDetalhesPage from "./pages/NotificacaoDetalhesPage";
import TopicosPage from "./pages/TopicosPage";
import ConviteAgentePage from "./pages/ConviteAgentePage";
import CadastroAgentePage from "./pages/CadastroAgentePage";
import AgenteDetalhesPage from "./pages/AgenteDetalhesPage";
import PerfilPage from "./pages/PerfilPage";
import InviteTokenUsedPage from "./pages/InviteTokenUsedPage";
import LoginPage from "./pages/auth/LoginPage";
import CriarContaPage from "./pages/auth/CriarContaPage";
import RedefinirSenhaPage from "./pages/auth/RedefinirSenhaPage";
import EsqueceuSenhaPage from "./pages/EsqueceuSenhaPage";
import AutenticacaoDoisFatoresPage from "./pages/AutenticacaoDoisFatoresPage";
import CriarOuvidoriaPage from "./pages/CriarOuvidoriaPage";
import EditarOuvidoriaPage from "./pages/EditarOuvidoriaPage";
import ConfigurarCategoriasPage from "./pages/ConfigurarCategoriasPage";
import ReportarProblemaPage from "./pages/ReportarProblemaPage";
import SaibaMaisReportarPage from "./pages/SaibaMaisReportarPage";
import ConfiguracoesPage from "./pages/ConfiguracoesPage";
import GraphsPage from "./pages/GraphsPage";
// import ValidacoesPage from "./pages/ValidacoesPage";
import PoliticaPrivacidadePage from "./pages/PoliticaPrivacidadePage";
import TermosUsoPage from "./pages/TermosUsoPage";
import NotFound from "./pages/NotFound";
import LandingPage from "./pages/LandingPage";
import QuemSomosPage from "./pages/QuemSomosPage";
import SolicitarOrcamentoPage from "./pages/SolicitarOrcamentoPage";

const queryClient = new QueryClient();

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/quem-somos" element={<QuemSomosPage />} />
      <Route path="/solicitar-orcamento" element={<SolicitarOrcamentoPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout>
            <DashboardPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/solicitacoes" element={
        <ProtectedRoute>
          <DashboardLayout>
            <SolicitacoesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/solicitacoes/:id" element={
        <ProtectedRoute>
          <DashboardLayout><SolicitacaoDetalhesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/notificacoes/:id" element={
        <ProtectedRoute>
          <DashboardLayout><NotificacaoDetalhesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/agentes" element={
        <ProtectedRoute>
          <DashboardLayout>
            <AgentesErrorBoundary>
              <AgentesPage />
            </AgentesErrorBoundary>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/agentes/:id" element={
        <ProtectedRoute>
          <DashboardLayout><AgenteDetalhesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route
        path="/ouvidoria"
        element={
          <GestorRoute>
            <DashboardLayout>
              <OuvidoriaPage />
            </DashboardLayout>
          </GestorRoute>
        }
      />
      <Route path="/ouvidorias-supervisionadas" element={
        <ProtectedRoute>
          <DashboardLayout><OuvidoriasSupervisionadasPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/ouvidorias-supervisionadas/:id" element={
        <ProtectedRoute>
          <DashboardLayout><OuvidoriaSupervisionadaDetalhesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/supervisora" element={
        <ProtectedRoute>
          <DashboardLayout><SupervisoraPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/topicos" element={
        <ProtectedRoute>
          <DashboardLayout><TopicosPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/relatorios" element={
        <ProtectedRoute>
          <DashboardLayout><RelatoriosPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/graficos" element={
        <ProtectedRoute>
          <DashboardLayout><GraphsPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/perfil" element={
        <ProtectedRoute>
          <DashboardLayout><PerfilPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/configuracoes" element={
        <ProtectedRoute>
          <DashboardLayout><ConfiguracoesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      {/* Desabilitado temporariamente: Validações
      <Route path="/validacoes" element={
        <ProtectedRoute>
          <DashboardLayout><ValidacoesPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/criar-conta" element={<GestorRoute><CriarContaPage /></GestorRoute>} />
      <Route path="/esqueceu-senha" element={<EsqueceuSenhaPage />} />
      <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />
      <Route path="/autenticacao-dois-fatores" element={<AutenticacaoDoisFatoresPage />} />
      <Route path="/criar-ouvidoria" element={<CriarOuvidoriaPage />} />
      <Route path="/configurar-categorias" element={<ConfigurarCategoriasPage />} />
      <Route path="/editar-ouvidoria" element={<EditarOuvidoriaPage />} />
      <Route path="/cadastro-agente" element={<CadastroAgentePage />} />
      <Route path="/convite-agente" element={
        <ProtectedRoute>
          <DashboardLayout><ConviteAgentePage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/convite-usado" element={<InviteTokenUsedPage />} />
      <Route path="/reportar-problema" element={
        <ProtectedRoute>
          <DashboardLayout><ReportarProblemaPage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/saiba-mais-reportar" element={<SaibaMaisReportarPage />} />
      <Route path="/politica-privacidade" element={<PoliticaPrivacidadePage />} />
      <Route path="/termos-uso" element={<TermosUsoPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={googleClientId || ''}>
        <AuthProvider>
          <ThemeProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </TooltipProvider>
          </ThemeProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
